import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  Avatar,
  Table,
  Select,
  Button
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/pro-duotone-svg-icons';
import { notificationsAtom, myCommissionsHeaderAtom } from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { toCurrency, toFormattedLocalDate, toFormattedLocalTime } from '../../../../functions';
import Card from '../../../shared-components/container/Card';

function MyCommissionsTable({ filter }) {
  const { t } = useTranslation();
  const { Option } = Select;
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const headerInfo = useRecoilValue(myCommissionsHeaderAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSize, setCurrentSize] = useState(20);
  const [selectedSort, setSelectedSort] = useState(0);

  const closerColumns = [
    {
      title: '',
      align: 'left',
      className: 'text-sm w-full md:hidden',
      render: (row) => (
        <div className="grid grid-cols-2 gap-2 w-full">
          <div className="col-span-2 text-center">
            <p className="font-bold">{row?.externalDealId}</p>
            <p className="font-bold">{t('client')}:</p>
            <span>
              {row?.submittedBy?.completeName}
            </span>
          </div>
          <div className="col-span-2 text-center">
            <p className="font-bold">{t('setter')}:</p>
            <>
              {row?.setter && (
                <span>
                  <Avatar src={row?.setter?.avatar} className=" bg-primaryColor h12 w-8 mr-2" />
                  {row?.setter?.completeName}
                </span>
              )}
              {!row?.setter && (
                <span>{t('self-gen')}</span>
              )}
            </>
          </div>
          <div className="col-span-2 text-center">
            <p className="font-bold">{t('date')}:</p>
            <p>{row.payoutDate ? toFormattedLocalDate(row.payoutDate) : '-'}</p>
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('total_commission')}:</p>
            <p>{row.closerCommisionAmount ? toCurrency(row.closerCommisionAmount) : '-'}</p>
          </div>
          <div className="col-span-1 text-right">
            <p className="font-bold">{t('setter_commission')}:</p>
            <p>{row.closerCommisionAmount ? toCurrency(row.setterCommisionAmount) : '-'}</p>
          </div>
          <div className="col-span-2 text-center">
            <p className="font-bold">{t('effective_commission')}:</p>
            <p>{row.effectiveCloserCommisionAmount ? toCurrency(row.effectiveCloserCommisionAmount) : '-'}</p>
          </div>
        </div>
      ),
    },
    {
      title: t('client'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>
          {/* <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" /> */}
          {row?.submittedBy?.completeName}
        </span>
      ),
    },
    {
      title: t('deal_id'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>
          {row?.externalDealId}
        </span>
      ),
    },
    {
      title: t('setter'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <>
          {row?.setter && (
            <span>
              <Avatar src={row?.setter?.avatar} className=" bg-primaryColor h12 w-8 mr-2" />
              {row?.setter?.completeName}
            </span>
          )}
          {!row?.setter && (
            <span>{t('self-gen')}</span>
          )}
        </>
      ),
    },
    {
      title: t('date'),
      width: 200,
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{row.payoutDate ? toFormattedLocalDate(row.payoutDate) : '-'}</span>
      ),
    },
    {
      title: t('total_commission'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{row.closerCommisionAmount ? toCurrency(row.closerCommisionAmount) : '-'}</span>
      ),
    },
    {
      title: t('setter_commission'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{row.setterCommisionAmount ? toCurrency(row.setterCommisionAmount) : '-'}</span>
      ),
    },
    {
      title: t('effective_commission'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{row.effectiveCloserCommisionAmount ? toCurrency(row.effectiveCloserCommisionAmount) : '-'}</span>
      ),
    },
  ];
  const setterColumns = [
    {
      title: '',
      align: 'left',
      className: 'text-sm w-full md:hidden',
      render: (row) => (
        <div className="grid grid-cols-2 gap-2 w-full text-center">
          <div className="col-span-2">
            <p className="font-bold">{row?.externalDealId}</p>
            <p className="font-bold">{t('client')}:</p>
            <span>
              {row?.submittedBy?.completeName}
            </span>
          </div>
          <div className="col-span-2">
            <p className="font-bold">{t('closer')}:</p>
            <>
              {_.filter(row?.assignments, (a) => a.ownershipType === 0).length > 0 && (
                <span>
                  <Avatar src={_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                  {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.completeName}
                </span>
              )}
              {_.filter(row?.assignments, (a) => a.ownershipType === 0).length === 0 && (
                <span>-</span>
              )}
            </>
          </div>
          <div className="col-span-1 text-left">
            <p className="font-bold">{t('date')}:</p>
            <span>{row.payoutDate ? toFormattedLocalDate(row.payoutDate) : '-'}</span>
          </div>
          <div className="col-span-1 text-right">
            <p className="font-bold">{t('amount')}:</p>
            <span>{row.setterCommisionAmount ? toCurrency(row.setterCommisionAmount) : '-'}</span>
          </div>
        </div>
      ),
    },
    {
      title: t('client'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>
          {/* <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" /> */}
          {row?.submittedBy?.completeName}
        </span>
      ),
    },
    {
      title: t('deal_id'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>
          {row?.externalDealId}
        </span>
      ),
    },
    {
      title: t('closer'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <>
          {_.filter(row?.assignments, (a) => a.ownershipType === 0).length > 0 && (
            <span>
              <Avatar src={_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
              {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.completeName}
            </span>
          )}
          {_.filter(row?.assignments, (a) => a.ownershipType === 0).length === 0 && (
            <span>-</span>
          )}
        </>
      ),
    },
    {
      title: t('date'),
      width: 200,
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{row.payoutDate ? toFormattedLocalDate(row.payoutDate) : '-'}</span>
      ),
    },
    {
      title: t('amount'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{row.setterCommisionAmount ? toCurrency(row.setterCommisionAmount) : '-'}</span>
      ),
    },
  ];

  function getCloserCardAmount() {
    let effective = 0;
    let closer = 0;
    let setter = 0;
    if (filter === 0 || filter === 1) {
      effective = headerInfo?.paidTotal;
      setter = headerInfo?.paidTotalSetter;
      closer = headerInfo?.paidTotalCloser;
    } else if (filter === 2 || filter === 3) {
      effective = headerInfo?.scheduledTotal;
      setter = headerInfo?.scheduledTotalSetter;
      closer = headerInfo?.scheduledTotalCloser;
    } else {
      effective = headerInfo?.noDateTotal;
      setter = headerInfo?.noDateTotalSetter;
      closer = headerInfo?.noDateTotalCloser;
    }
    return {
      effective: toCurrency(effective),
      closer: toCurrency(closer),
      setter: toCurrency(setter)
    };
  }

  function getSetterCardAmount() {
    let amount = 0;
    if (filter === 0 || filter === 1) {
      amount = headerInfo?.paidTotalSetter;
    } else if (filter === 2 || filter === 3) {
      amount = headerInfo?.scheduledTotalSetter;
    } else {
      amount = headerInfo?.noDateTotalSetter;
    }
    return toCurrency(amount);
  }

  function getColumns() {
    if (filter === 0 || filter === 2 || filter === 4) {
      return closerColumns;
    }
    return setterColumns;
  }

  function getData(page) {
    setLoading(true);
    setCurrentPage(page);
    api.post('reports/compensation/me', {
      page,
      size: currentSize,
      filter,
      sort: selectedSort
    })
    .then((response) => {
      setLoading(false);
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response?.data?.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setLoading(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error?.message,
          error: true,
        },
      ]);
    })
  }

  useEffect(() => {
    getData(0);
  }, []);

  useEffect(() => {
    getData(0);
  }, [currentSize, selectedSort]);

  return (
    <div>
      <div className="flex space-x-2">
        <Select
          className="h-12 mb-2 ml-auto"
          value={selectedSort}
          disabled={loading}
          onChange={(obj) => setSelectedSort(obj)}
        >
          <Option value={0}>{t('descending')} <FontAwesomeIcon className="ml-2" icon={faArrowDown} /></Option>
          <Option value={1}>{t('ascending')} <FontAwesomeIcon className="ml-2" icon={faArrowUp} /></Option>
        </Select>
        <Button
          className="mt-2"
          type="primary"
          onClick={() => getData(currentPage)}
        >
          {t('refresh')}
        </Button>
      </div>
      {(filter === 0 || filter === 2 || filter === 4) && (
        <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-2">
          <Card
            className="w-full border-4 rounded-sm col-span-1 md:col-span-2 lg:col-span-1"
            title={t('amount')}
            description={getCloserCardAmount().closer}
            descriptionSize="text-2xl"
            textPosition="center"
          />
          <Card
            className="w-full border-4 rounded-sm col-span-1"
            title={t('setter_commission')}
            description={getCloserCardAmount().setter}
            descriptionSize="text-2xl"
            textPosition="center"
          />
          <Card
            className="w-full border-4 rounded-sm col-span-1"
            title={t('effective_commission')}
            description={getCloserCardAmount().effective}
            descriptionSize="text-2xl"
            textPosition="center"
          />
        </div>
      )}
      {(filter === 1 || filter === 3 || filter === 5) && (
        <div className="mb-2">
          <Card
            className="w-full border-4 rounded-sm"
            title={t('amount')}
            description={getSetterCardAmount()}
            descriptionSize="text-2xl"
            textPosition="center"
          />
        </div>
      )}
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        className="w-full"
        rowKey="id"
        pagination={{
          pageSize: currentSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: data?.total,
          onChange: (page, size) => {
            if (page - 1 !== currentPage) {
              getData(page - 1);
            } else {
              setCurrentSize(size);
            }
          }
        }}
        loading={loading}
        dataSource={data?.leads}
        columns={getColumns()}
      />
    </div>
   );
}

export default MyCommissionsTable;
